import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AttachmentStrip,
  imageFiles,
  MAX_ATTACHMENTS,
  type Attachment,
} from './Attachments';
import { ANIM, COLOR, FONT, G } from '../styles/tokens';
import type { NoraActiveContext } from '../types';

export interface ModelOption {
  id: string;
  label: string;
  note?: string;
}

interface Props {
  onSend: (text: string) => void;
  onStop?: () => void;
  streaming: boolean;
  disabled?: boolean;
  models: ModelOption[];
  modelError: string | null;
  selectedModel: string;
  onModelChange: (id: string) => void;
  attachments: Attachment[];
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  isMobile: boolean;
  variant?: 'docked' | 'hero';
  context: NoraActiveContext;
  onRemoveContext: (key: keyof NoraActiveContext) => void;
}

export function Composer({
  onSend,
  onStop,
  streaming,
  disabled = false,
  models,
  modelError,
  selectedModel,
  onModelChange,
  attachments,
  onAddFiles,
  onRemoveAttachment,
  isMobile,
  variant = 'docked',
  context,
  onRemoveContext,
}: Props) {
  const [text, setText] = useState('');
  const [modelOpen, setModelOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);

  const readyAttachments = attachments.filter(
    attachment => attachment.status === 'ready',
  ).length;
  const attachmentsLoading = attachments.some(
    attachment => attachment.status === 'loading',
  );
  const isHero = variant === 'hero';
  const buttonSize = isMobile ? 42 : 36;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  }, [text]);

  useEffect(() => {
    if (!isMobile) textareaRef.current?.focus();
  }, [isMobile]);

  useEffect(() => {
    if (!modelOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (!modelRef.current?.contains(event.target as Node)) {
        setModelOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [modelOpen]);

  const submit = useCallback(() => {
    const value = text.trim();
    if (
      (!value && readyAttachments === 0) ||
      attachmentsLoading ||
      streaming ||
      disabled
    ) {
      return;
    }
    setText('');
    onSend(value);
  }, [attachmentsLoading, disabled, onSend, readyAttachments, streaming, text]);

  const canSend =
    (text.trim().length > 0 || readyAttachments > 0) &&
    !attachmentsLoading &&
    !streaming &&
    !disabled;
  const model = models.find(entry => entry.id === selectedModel);
  const contextEntries: {
    key: keyof NoraActiveContext;
    label: string;
    value: string;
  }[] = [
    context.cluster
      ? { key: 'cluster', label: 'Cluster', value: context.cluster }
      : null,
    context.namespace
      ? { key: 'namespace', label: 'Namespace', value: context.namespace }
      : null,
    context.kind && context.name
      ? {
          key: 'name',
          label: context.kind,
          value: context.name,
        }
      : null,
    context.entityRef
      ? { key: 'entityRef', label: 'Entity', value: context.entityRef }
      : null,
  ].filter(
    (
      entry,
    ): entry is {
      key: keyof NoraActiveContext;
      label: string;
      value: string;
    } => entry !== null,
  );

  const textarea = (
    <textarea
      ref={textareaRef}
      disabled={disabled}
      value={text}
      onChange={event => setText(event.target.value)}
      onKeyDown={event => {
        if (event.key === 'Enter' && !event.shiftKey && !isMobile) {
          event.preventDefault();
          submit();
        }
        if (event.key === 'Escape' && streaming) {
          event.preventDefault();
          onStop?.();
        }
        if (
          event.key === 'Backspace' &&
          text === '' &&
          attachments.length > 0
        ) {
          event.preventDefault();
          onRemoveAttachment(attachments[attachments.length - 1].id);
        }
      }}
      onPaste={event => {
        const files = imageFiles(event.clipboardData);
        if (!files.length) return;
        event.preventDefault();
        onAddFiles(files);
      }}
      rows={1}
      placeholder={
        disabled
          ? 'Loading conversation...'
          : streaming
          ? 'Nora is responding...'
          : attachments.length
          ? 'Ask about these images'
          : 'Ask Nora'
      }
      aria-label="Ask Nora"
      style={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        padding: isHero ? '10px 6px' : '10px 14px 6px',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        resize: 'none',
        maxHeight: 220,
        overflowY: 'auto',
        color: COLOR.textPrimary,
        caretColor: COLOR.blue,
        fontFamily: FONT.sans,
        fontSize: isMobile ? 16 : FONT.size.md,
        lineHeight: FONT.lineHeight.base,
      }}
    />
  );

  const attachButton = (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={attachments.length >= MAX_ATTACHMENTS}
      title={
        attachments.length >= MAX_ATTACHMENTS
          ? `Maximum ${MAX_ATTACHMENTS} images`
          : 'Add images'
      }
      aria-label="Add images"
      style={{
        width: buttonSize,
        height: buttonSize,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: 'transparent',
        color:
          attachments.length >= MAX_ATTACHMENTS
            ? COLOR.textInactive
            : COLOR.textPrimary,
        cursor: attachments.length >= MAX_ATTACHMENTS ? 'default' : 'pointer',
        fontSize: 26,
        fontWeight: 300,
        lineHeight: 1,
      }}
    >
      +
    </button>
  );

  const modelButton = (
    <button
      type="button"
      onClick={() => setModelOpen(open => !open)}
      disabled={models.length === 0}
      title={modelError ?? 'Switch model'}
      aria-expanded={modelOpen}
      style={{
        height: buttonSize,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '0 10px',
        border: 'none',
        borderRadius: buttonSize / 2,
        background: 'transparent',
        color: models.length ? COLOR.textPrimary : COLOR.textMuted,
        cursor: models.length ? 'pointer' : 'default',
        fontFamily: FONT.sans,
        fontSize: FONT.size.sm,
      }}
    >
      <span>
        {model?.label ?? (modelError ? 'Models unavailable' : 'Loading models')}
      </span>
      <span style={{ color: COLOR.textMuted, fontSize: 10 }}>⌄</span>
    </button>
  );

  const actionButton = streaming ? (
    <button
      type="button"
      onClick={onStop}
      title="Stop"
      aria-label="Stop"
      style={{
        width: buttonSize,
        height: buttonSize,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: COLOR.bgOverlay,
        color: COLOR.textPrimary,
        cursor: 'pointer',
        fontSize: 10,
      }}
    >
      {G.stop}
    </button>
  ) : (
    <button
      type="button"
      onClick={submit}
      disabled={!canSend}
      title="Send"
      aria-label="Send"
      style={{
        width: buttonSize,
        height: buttonSize,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: canSend ? COLOR.blue : 'transparent',
        color: canSend ? '#07111f' : COLOR.textInactive,
        cursor: canSend ? 'pointer' : 'default',
        fontSize: 17,
        fontWeight: 700,
      }}
    >
      {G.arrowUp}
    </button>
  );

  return (
    <div
      ref={modelRef}
      style={{
        position: 'relative',
        flexShrink: 0,
        padding: isHero ? 0 : isMobile ? '0 10px 8px' : '0 16px 10px',
      }}
    >
      {modelOpen && models.length > 0 && (
        <div
          style={{
            position: 'absolute',
            right: isMobile ? 10 : 16,
            bottom: '100%',
            zIndex: 10,
            minWidth: 210,
            marginBottom: 8,
            padding: 6,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 16,
            background: COLOR.bg,
            boxShadow: '0 10px 32px rgba(0,0,0,0.5)',
            animation: ANIM.fadeIn,
          }}
        >
          {models.map(entry => {
            const selected = entry.id === selectedModel;
            return (
              <button
                type="button"
                key={entry.id}
                onClick={() => {
                  onModelChange(entry.id);
                  setModelOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 10,
                  background: selected ? COLOR.bgOverlay : 'transparent',
                  color: COLOR.textPrimary,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: FONT.sans,
                }}
              >
                <span style={{ width: 14, color: COLOR.blue }}>
                  {selected ? '✓' : ''}
                </span>
                <span style={{ flex: 1, fontSize: FONT.size.md }}>
                  {entry.label}
                </span>
                <span
                  style={{ color: COLOR.textMuted, fontSize: FONT.size.xs }}
                >
                  {entry.note}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          padding: isHero ? '7px 10px' : '8px 10px 10px',
          border: `1px solid ${COLOR.borderSubtle}`,
          borderRadius: isHero ? 34 : 28,
          background: COLOR.bg,
          boxShadow: isHero
            ? '0 6px 22px rgba(0,0,0,0.4)'
            : '0 3px 14px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ padding: attachments.length ? '4px 8px 0' : 0 }}>
          <AttachmentStrip
            attachments={attachments}
            onRemove={onRemoveAttachment}
          />
        </div>
        {contextEntries.length > 0 && (
          <div
            aria-label="Active context"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '4px 8px 2px',
            }}
          >
            {contextEntries.map(entry => (
              <span
                key={`${entry.key}-${entry.value}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  maxWidth: '100%',
                  padding: '4px 7px',
                  border: `1px solid ${COLOR.borderSubtle}`,
                  borderRadius: 12,
                  background: COLOR.bgOverlay,
                  color: COLOR.textSecondary,
                  fontFamily: FONT.sans,
                  fontSize: FONT.size.xs,
                }}
              >
                <strong>{entry.label}</strong>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.value}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${entry.label} context`}
                  onClick={() => {
                    onRemoveContext(entry.key);
                    if (entry.key === 'name') onRemoveContext('kind');
                  }}
                  style={{
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: COLOR.textMuted,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={event => {
            onAddFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />

        {isHero ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {attachButton}
            {textarea}
            {modelButton}
            {actionButton}
          </div>
        ) : (
          <>
            {textarea}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '2px 4px 0',
              }}
            >
              {attachButton}
              <div style={{ flex: 1 }} />
              {modelButton}
              {actionButton}
            </div>
          </>
        )}
      </div>

      {!isHero && (
        <div
          style={{
            marginTop: 8,
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
            color: COLOR.textMuted,
            fontFamily: FONT.sans,
            fontSize: FONT.size.xs,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          Nora can make mistakes, so double-check important information.
        </div>
      )}
    </div>
  );
}
